import type { Doc } from '@ppwr/convex/_generated/dataModel'
import { CardString, Hand } from 'pokersolver'

type HandOutcome = Pick<Doc<'handHistory'>, 'winners' | 'communityCards'>

export function extractRankingCards(hand: Hand, holeCards: string[]): string[] {
  type Card = Hand['cards'][0]
  const cardToString = (card: Card) => card.value + card.suit.toLowerCase()
  const cards = hand.cards

  // Group cards by their rank value
  const rankGroups: { [rank: string]: Card[] } = {}
  cards.forEach((card) => {
    if (!rankGroups[card.value]) {
      rankGroups[card.value] = []
    }
    rankGroups[card.value].push(card)
  })

  // Get groups sorted by size (descending)
  const sortedGroups = Object.values(rankGroups).sort((a, b) => b.length - a.length)

  let rankingCards: string[] = []

  switch (hand.name) {
    case 'Royal Flush':
    case 'Straight Flush':
    case 'Flush':
    case 'Straight':
      // All 5 cards make up the hand
      rankingCards = cards.map(cardToString)
      break

    case 'Four of a Kind':
      // Find and return the 4 matching cards
      rankingCards = sortedGroups[0].map(cardToString) // Group with 4 cards
      break

    case 'Full House':
      // All 5 cards (3 of a kind + pair)
      rankingCards = cards.map(cardToString)
      break

    case 'Three of a Kind':
      // Find and return the 3 matching cards
      rankingCards = sortedGroups[0].map(cardToString) // Group with 3 cards
      break

    case 'Two Pair':
      // Return the 4 cards that make both pairs
      rankingCards = [...sortedGroups[0], ...sortedGroups[1]].map(cardToString) // Two groups with 2 cards each
      break

    case 'Pair':
      // Find and return the 2 matching cards
      rankingCards = sortedGroups[0].map(cardToString) // Group with 2 cards
      break

    case 'High Card':
      // Return just the high card (first card in the hand)
      rankingCards = cards
        .sort((a, b) => b.rank - a.rank)
        .map(cardToString)
        .slice(0, 1)
      break

    default:
      rankingCards = cards.map(cardToString)
  }

  // Ensure at least one hole card is shown if hole cards are in the best hand
  const bestHandCards = cards.map(cardToString)
  const holeCardsInBestHand = holeCards.filter((card) => bestHandCards.includes(card))

  if (holeCardsInBestHand.length > 0) {
    const holeCardsInRanking = holeCardsInBestHand.filter((card) => rankingCards.includes(card))

    // If no hole cards in ranking, add at least one from best hand
    if (holeCardsInRanking.length === 0) {
      rankingCards = [...rankingCards, holeCardsInBestHand[0]]
    }
  }

  return rankingCards
}

/**
 * Ranking cards to highlight for each winner of a hand.
 * A win by fold never reached showdown, so nothing is highlighted: doing so
 * would reveal what the winner held (value bet vs. bluff).
 */
export function getHighlightedWinnerCards(handOutcome: HandOutcome | undefined): string[][] {
  const winners = handOutcome?.winners ?? []
  if (winners.some((w) => w.handRank === 'Win by fold')) {
    return []
  }
  const communityCards = handOutcome?.communityCards ?? []
  return winners.map((w) => {
    const allCards = [...communityCards, ...w.cards] as CardString[]
    try {
      return extractRankingCards(Hand.solve(allCards), w.cards)
    } catch (error) {
      return []
    }
  })
}
